import type { ReadonlyDeep } from 'type-fest';

import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { ImpactPageDataQuery, ImpactPageDataQueryVariables } from './impactPageData.graphql';
import { ImpactPageData as ImpactPageDataQueryDocument } from './impactPageData.graphql';

type ImpactPageDataParams = { locale: string; countryCode: string };

type ImpactStory = ImpactPageDataQuery['impactStories'][number];

export type VideoStory = ReadonlyDeep<{
	id: string;
	lookupKey: string;
	title: string;
	signatureCount: number;
	signatureCountLocalized: string;
	videoUrl: string;
	posterUrl: string;
}>;

export type ImpactPageData = ReadonlyDeep<{ videoStories: readonly VideoStory[] }>;

function toVideoStory({
	id,
	lookupKey,
	title,
	signatureCount,
	signatureCountLocalized,
	videoUrl,
	imageUrl,
	bubbleImageUrl,
}: ImpactStory): VideoStory | undefined {
	if (!videoUrl) return undefined;

	// bubbleImageUrl is non-null in the schema, so every slide has a poster; a falsy check because the
	// FCM content carries an empty imageUrl for some stories
	return {
		id,
		lookupKey,
		title,
		signatureCount,
		signatureCountLocalized,
		videoUrl,
		posterUrl: imageUrl || bubbleImageUrl,
	};
}

/**
 * The params only key the page-data cache: the stories depend on locale and country through the
 * impact_stories FCM, which the middle-end resolves from the request itself.
 */
export async function getImpactPageData(
	utilityContext: UtilityContext,
	_params: ImpactPageDataParams,
): Promise<ImpactPageData> {
	try {
		const { data } = await utilityContext.gql.fetch<ImpactPageDataQuery, ImpactPageDataQueryVariables>({
			query: ImpactPageDataQueryDocument,
			variables: {},
			rejectOnError: true,
		});

		return {
			videoStories: data.impactStories.map(toVideoStory).filter((story) => story !== undefined),
		};
	} catch (error) {
		// the stories only feed the carousel, so a failure hides that one section instead of the whole page
		void utilityContext.errorReporter.report(error as Error, { severity: 'warning' });
		return { videoStories: [] };
	}
}
